import { useQuery, useQueryClient } from 'react-query';

import type { User, AccessorRole } from 'types';
import type { Permission } from 'types/permissions';
import type { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

import { getRequest } from './apiRequests';

export type GetCurrentUserResponse = User & { organization_name: string };
export type ListUsersResponse = {
  object: 'list';
  data: User[];
  total_count: number;
};

export type GetCurrentUserPermissionsResponse = {
  shared_resources: Array<
    Permission & {
      created_at: string;
      updated_at: string;
      id: string;
      object: 'shared_resource';
    }
  >;
  role: AccessorRole;
  can_user_be_assigned_role: boolean;
};

const getCurrentUserUrl = '/api/payee/profile';
const getCurrentUser = () => getRequest<null, GetCurrentUserResponse>(getCurrentUserUrl);

const getUsersUrl = (searchQuery: string) => `/api/users?searchQuery=${searchQuery}`;
const getUsers = (searchQuery: string) => getRequest<null, ListUsersResponse>(getUsersUrl(searchQuery));

const useGetCurrentUserQuery = (options: QueryHookOptions<GetCurrentUserResponse, APIError> = {}) => {
  return {
    query: useQuery<GetCurrentUserResponse, APIError>({
      ...options,
      queryKey: ['currentUser'],
      queryFn: () => getCurrentUser(),
    }),
  };
};

const getCurrentUserPermissions = () => getRequest<null, GetCurrentUserPermissionsResponse>(`/api/profile/permissions`);

const useGetCurrentUserPermissions = (options: QueryHookOptions<GetCurrentUserPermissionsResponse, APIError> = {}) => {
  return {
    query: useQuery<GetCurrentUserPermissionsResponse, APIError>({
      ...options,
      queryKey: ['currentUserPermissions'],
      queryFn: getCurrentUserPermissions,
    }),
  };
};

const useGetUsersQuery = (searchQuery: string, enabled = true) => {
  const QUERY_KEY = ['users', searchQuery];
  const queryClient = useQueryClient();

  const query = useQuery<ListUsersResponse, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => getUsers(searchQuery),
    enabled,
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

const searchUsersUrl = (searchQuery: string) =>
  `/api/v1/users/permissions/shared-resources/search-accessors/?query=${searchQuery}&ordering=first_name%2Clast_name&include_hierarchy_groups=false`;
const searchUsers = (searchQuery: string) => getRequest<null, ListUsersResponse>(searchUsersUrl(searchQuery));

type SearchUsersOptions = {
  enabled?: boolean;
  keepPreviousData?: boolean;
};

const useSearchUsersQuery = (searchQuery: string, options: SearchUsersOptions = { enabled: true }) => {
  const QUERY_KEY = ['search-users', searchQuery];
  const queryClient = useQueryClient();

  const query = useQuery<ListUsersResponse, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => searchUsers(searchQuery),
    ...options,
    enabled: !!searchQuery && options.enabled,
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

export const UsersService = {
  useGetCurrentUserQuery,
  useGetUsersQuery,
  useSearchUsersQuery,
  useGetCurrentUserPermissions,
};
